// F3 (#809) — preserve the device's ps_crud upload queue to the durable recovery
// store on sign-out, then AUDIT the outcome. Split out of auth-provider.tsx (the
// quarantine equivalent, preserveHeldThenAudit, lives there) to keep that file
// under the 500-line gate, and so the Tier-1 drain + Tier-2 rescue + audit are
// unit-testable without rendering the whole provider.
//
// THE LOSS THIS CLOSES. `disconnectAndClear` wipes the ps_crud upload queue on
// sign-out. The H8/H9 rescue (preserveHeldThenAudit) ships ONLY the
// `orphaned_capture_items` quarantine — the un-uploaded-but-syncable queue (the
// "H10" dimension) was discarded. A surveyor who confirms sign-out with a queued
// backlog permanently lost those contractual counts (Sentry `1P`: 7,119 ops on
// one logout). This module rescues that queue to the same isolated inbox BEFORE
// the clear, so a confirmed discard is no longer a silent permanent loss.
//
// BEST-EFFORT + BOUNDED, like the quarantine rescue: it NEVER throws and NEVER
// traps the surveyor on a shared device — a wedged worker / offline device is
// audited (NOT silent) and the sign-out proceeds. Tier 1 (drain-first) is a pure
// optimisation; Tier 2 (rescue) is the guarantee. Wired into proceedLogout()
// after preserveHeldThenAudit(), gated on a known, non-empty ps_crud queue.

import * as Sentry from '@sentry/react'

import {
  preserveUploadQueueBeforeClear,
  waitForUploadQueueToSettle,
} from '../hooks/powersync/logout-cleanup'
import type { RescueResult } from '../hooks/powersync/rescue-held-captures'

// Tier-1 best-effort drain bound: long enough for an in-flight online upload to
// finish, short enough to leave the Tier-2 rescue ample room inside the caller's
// overall sign-out budget.
const QUEUE_DRAIN_TIMEOUT_MS = 2_000

// Tier-2 rescue bound: a local SELECT + a chunked POST. Like the quarantine
// rescue bound, it only trips for a wedged worker / stalled network; on timeout
// we audit and proceed, never trapping the surveyor.
const QUEUE_RESCUE_TIMEOUT_MS = 6_000

/**
 * Rescue the device's ps_crud-queued capture counts to the recovery store, then
 * audit. `queuedCount` is the pre-clear `getHeldCaptureLoad().uploadQueueCount`
 * (what the clear would otherwise discard) — used only for the audit copy; it is
 * `null` on an INDETERMINATE device, where the queue COUNT is unreadable
 * (`getUploadQueueStats` threw) but the queue itself may still be rescuable via
 * `getCrudBatch` (a different read), so we still attempt the rescue (audit bh-3 /
 * ps-F). Never throws.
 */
export async function preserveUploadQueueThenAudit(
  queuedCount: number | null,
): Promise<void> {
  // Tier 1 — let an online queue drain so Tier 2 ships only what truly can't
  // sync. Best-effort: it never throws, and a queue that doesn't drain (offline /
  // wedged) just falls through to the Tier-2 rescue below.
  try {
    await waitForUploadQueueToSettle(QUEUE_DRAIN_TIMEOUT_MS)
  } catch {
    // best-effort — Tier 2 is the guarantee
  }

  // Tier 2 — ship whatever remains queued to the durable inbox, bounded. `result`
  // stays null on BOTH the timeout arm and a thrown rescue, so `result !== null`
  // precisely means "the rescue resolved" (mirrors preserveHeldThenAudit).
  let result: RescueResult | null = null
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    result = await Promise.race<RescueResult | null>([
      preserveUploadQueueBeforeClear(),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => {
          resolve(null)
        }, QUEUE_RESCUE_TIMEOUT_MS)
      }),
    ])
  } catch {
    // Best-effort: a failed rescue (offline / server error) is audited below as a
    // discard, never swallowed silently.
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }

  if (result !== null) {
    // The queued counts are now durable in the recovery inbox — the clear's
    // wipe of ps_crud no longer loses them.
    Sentry.captureMessage(
      `logout: preserved ${result.sent}/${result.found} ps_crud-queued capture ` +
        `count(s) to the recovery store before the clear (queue rescue, F3/issue 809)`,
      'info',
    )
  } else {
    // Offline / wedged / timed out — the clear WILL discard the queue from this
    // device. Audited at warning so Sentry severity filtering surfaces the loss.
    // This IS the fail-closed scaffold for the offline-logout case: the loss is
    // LOUD (audited), never silent. TIER 3 (#809) — an on-device durable stash
    // that survives the clear and re-injects next login — would close this last
    // gap, but it keeps un-uploaded contractual data on a SHARED device past
    // sign-out: a data-retention / PII decision (same class as the session
    // lifetime). DELIBERATELY NOT SHIPPED here — flagged in the PR for product +
    // security sign-off rather than landing silently. Until then the offline
    // queue rides the device's own ps_crud backup only until the clear.
    const count = queuedCount === null ? 'an unverifiable number of' : queuedCount
    Sentry.captureMessage(
      `logout: could NOT rescue ${count} ps_crud-queued capture count(s) to ` +
        `the recovery store before clearing (offline / wedged / timed out); the ` +
        `clear will discard them from this device (F3/issue 809)`,
      'warning',
    )
  }
}
